/* ==========================================================================
   Canvas Animation System - Ultimate Fallback for Windows Animation Issues
   Asian American Forward Website
   ========================================================================== */

let canvasSupported = false;
let animationCanvas = null;
let canvasContext = null;
let canvasAnimations = [];
let animationFrameId = null;

/* ==========================================================================
   Canvas Initialization
   ========================================================================== */

export function initializeCanvasAnimations() {
  console.log('🎨 Initializing Canvas Animation System...');
  
  // Check if canvas is supported
  const testCanvas = document.createElement('canvas');
  canvasSupported = !!(testCanvas.getContext && testCanvas.getContext('2d'));
  
  if (!canvasSupported) {
    console.warn('❌ Canvas not supported');
    return false;
  }
  
  // Create overlay canvas for animations
  createAnimationCanvas();
  
  console.log('✅ Canvas Animation System Ready - Works regardless of ANY settings');
  return true;
}

function createAnimationCanvas() {
  animationCanvas = document.createElement('canvas');
  animationCanvas.id = 'animation-overlay';
  animationCanvas.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    pointer-events: none;
    z-index: 1000;
    opacity: 0.8;
  `;
  
  canvasContext = animationCanvas.getContext('2d');
  
  // Append to body
  document.body.appendChild(animationCanvas);
  
  // Handle resize
  resizeCanvas();
  window.addEventListener('resize', resizeCanvas);
  
  // Start animation loop
  startAnimationLoop();
}

function resizeCanvas() {
  if (!animationCanvas) return;
  
  const dpr = window.devicePixelRatio || 1;
  const rect = animationCanvas.getBoundingClientRect();
  
  animationCanvas.width = rect.width * dpr;
  animationCanvas.height = rect.height * dpr;
  
  canvasContext.scale(dpr, dpr);
  animationCanvas.style.width = rect.width + 'px';
  animationCanvas.style.height = rect.height + 'px';
}

/* ==========================================================================
   Animation Loop
   ========================================================================== */

function startAnimationLoop() {
  function animate() {
    if (!canvasContext) return;
    
    // Clear canvas
    canvasContext.clearRect(0, 0, animationCanvas.width, animationCanvas.height);
    
    // Update and render all animations
    canvasAnimations = canvasAnimations.filter(animation => {
      const shouldContinue = updateAnimation(animation);
      if (shouldContinue) {
        renderAnimation(animation);
      }
      return shouldContinue;
    });
    
    animationFrameId = requestAnimationFrame(animate);
  }
  
  animate();
}

function updateAnimation(animation) {
  const now = performance.now();
  const elapsed = now - animation.startTime;
  const progress = Math.min(elapsed / animation.duration, 1);
  
  // Apply easing
  animation.progress = animation.easing(progress);
  
  // Update properties
  Object.keys(animation.properties).forEach(prop => {
    const start = animation.startValues[prop];
    const end = animation.endValues[prop];
    animation.currentValues[prop] = start + (end - start) * animation.progress;
  });
  
  // Check if animation is complete
  if (progress >= 1) {
    if (animation.onComplete) {
      animation.onComplete();
    }
    return false; // Remove animation
  }
  
  return true; // Continue animation
}

function renderAnimation(animation) {
  switch (animation.type) {
    case 'rainbow-border':
      renderRainbowBorder(animation);
      break;
    case 'particle-trail':
      renderParticleTrail(animation);
      break;
    case 'loading-indicator':
      renderLoadingIndicator(animation);
      break;
    case 'pulse-effect':
      renderPulseEffect(animation);
      break;
  }
}

/* ==========================================================================
   Animation Types
   ========================================================================== */

export function createRainbowBorderAnimation(element) {
  if (!canvasSupported) return null;
  
  const rect = element.getBoundingClientRect();
  const colors = [
    '#084874', // Blue
    '#3c4f82', // Blue to purple transition
    '#782b8b', // Purple
    '#8b2a5a', // Purple to red transition
    '#8f2226', // Red
    '#5b4650'  // Red to blue transition
  ]; /* Updated with smoother intermediate colors for seamless transitions */
  
  const animation = {
    type: 'rainbow-border',
    element: element,
    startTime: performance.now(),
    duration: 3000,
    progress: 0,
    easing: t => t, // Linear
    rect: rect,
    colors: colors,
    colorIndex: 0,
    properties: { colorIndex: 0 },
    startValues: { colorIndex: 0 },
    endValues: { colorIndex: colors.length - 1 },
    currentValues: { colorIndex: 0 }
  };
  
  canvasAnimations.push(animation);
  return animation;
}

function renderRainbowBorder(animation) {
  const rect = animation.element.getBoundingClientRect();
  const colorIndex = Math.floor(animation.currentValues.colorIndex) % animation.colors.length;
  const color = animation.colors[colorIndex];
  
  canvasContext.strokeStyle = color;
  canvasContext.lineWidth = 4;
  canvasContext.strokeRect(
    rect.left,
    rect.top,
    rect.width,
    rect.height
  );
}

export function createParticleTrailAnimation(x, y) {
  if (!canvasSupported) return null;
  
  const particles = [];
  for (let i = 0; i < 20; i++) {
    particles.push({
      x: x + (Math.random() - 0.5) * 100,
      y: y + (Math.random() - 0.5) * 100,
      vx: (Math.random() - 0.5) * 5,
      vy: (Math.random() - 0.5) * 5,
      life: 1,
      decay: 0.02
    });
  }
  
  const animation = {
    type: 'particle-trail',
    startTime: performance.now(),
    duration: 2000,
    progress: 0,
    easing: t => t,
    particles: particles,
    properties: { life: 1 },
    startValues: { life: 1 },
    endValues: { life: 0 },
    currentValues: { life: 1 }
  };
  
  canvasAnimations.push(animation);
  return animation;
}

function renderParticleTrail(animation) {
  animation.particles.forEach(particle => {
    particle.x += particle.vx;
    particle.y += particle.vy;
    particle.life -= particle.decay;
    
    if (particle.life > 0) {
      canvasContext.globalAlpha = particle.life;
      canvasContext.fillStyle = '#0088ff';
      canvasContext.beginPath();
      canvasContext.arc(particle.x, particle.y, 3, 0, Math.PI * 2);
      canvasContext.fill();
    }
  });
  
  canvasContext.globalAlpha = 1;
}

export function createLoadingIndicatorAnimation(x, y) {
  if (!canvasSupported) return null;
  
  const animation = {
    type: 'loading-indicator',
    startTime: performance.now(),
    duration: Infinity, // Continuous
    progress: 0,
    easing: t => t,
    x: x,
    y: y,
    properties: { rotation: 0 },
    startValues: { rotation: 0 },
    endValues: { rotation: 360 },
    currentValues: { rotation: 0 }
  };
  
  canvasAnimations.push(animation);
  return animation;
}

function renderLoadingIndicator(animation) {
  const x = animation.x;
  const y = animation.y;
  const rotation = (animation.currentValues.rotation * Math.PI) / 180;
  
  canvasContext.save();
  canvasContext.translate(x, y);
  canvasContext.rotate(rotation);
  
  // Draw loading spinner
  canvasContext.strokeStyle = '#0088ff';
  canvasContext.lineWidth = 3;
  canvasContext.lineCap = 'round';
  
  for (let i = 0; i < 8; i++) {
    const alpha = (i + 1) / 8;
    canvasContext.globalAlpha = alpha;
    canvasContext.beginPath();
    canvasContext.moveTo(0, -20);
    canvasContext.lineTo(0, -15);
    canvasContext.stroke();
    canvasContext.rotate(Math.PI / 4);
  }
  
  canvasContext.restore();
  canvasContext.globalAlpha = 1;
}

export function createPulseEffectAnimation(element) {
  if (!canvasSupported) return null;
  
  const rect = element.getBoundingClientRect();
  
  const animation = {
    type: 'pulse-effect',
    element: element,
    startTime: performance.now(),
    duration: 2000,
    progress: 0,
    easing: t => Math.sin(t * Math.PI * 2), // Sine wave for pulse
    rect: rect,
    properties: { opacity: 0.5 },
    startValues: { opacity: 0.5 },
    endValues: { opacity: 1 },
    currentValues: { opacity: 0.5 }
  };
  
  canvasAnimations.push(animation);
  return animation;
}

function renderPulseEffect(animation) {
  const rect = animation.element.getBoundingClientRect();
  const opacity = animation.currentValues.opacity;
  
  canvasContext.globalAlpha = opacity * 0.2; // Make it subtle
  canvasContext.fillStyle = '#0088ff';
  canvasContext.fillRect(rect.left, rect.top, rect.width, rect.height);
  canvasContext.globalAlpha = 1;
}

/* ==========================================================================
   Utility Functions
   ========================================================================== */

export function stopCanvasAnimation(animation) {
  if (!animation) return;
  
  const index = canvasAnimations.indexOf(animation);
  if (index > -1) {
    canvasAnimations.splice(index, 1);
  }
}

export function stopAllCanvasAnimations() {
  canvasAnimations.length = 0;
}

export function hideCanvasOverlay() {
  if (animationCanvas) {
    animationCanvas.style.display = 'none';
  }
}

export function showCanvasOverlay() {
  if (animationCanvas) {
    animationCanvas.style.display = 'block';
  }
}

export function isCanvasSupported() {
  return canvasSupported;
}

/* ==========================================================================
   Easy Integration Functions
   ========================================================================== */

export function animateElementWithCanvas(element, animationType = 'pulse-effect') {
  if (!canvasSupported) return null;
  
  switch (animationType) {
    case 'rainbow-border':
      return createRainbowBorderAnimation(element);
    case 'pulse-effect':
      return createPulseEffectAnimation(element);
    default:
      return createPulseEffectAnimation(element);
  }
}

export function addClickTrailEffect() {
  if (!canvasSupported) return;
  
  document.addEventListener('click', (e) => {
    // Skip click trail for modal event cards to prevent interference
    if (e.target.closest('.modal-event-card') || e.target.closest('.event-modal')) {
      return;
    }
    createParticleTrailAnimation(e.clientX, e.clientY);
  });
}

console.log('🎨 Canvas Animation System Loaded - Ultimate Fallback for ALL Animation Issues'); 